import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ClientLayout from '../../components/layouts/ClientLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Calendar, CreditCard, Bell, CheckCircle2, 
  ArrowRight, Loader2, Clock, MapPin, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import CountdownWidget from '../../components/CountdownWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDashboard = () => {
  const { user, event } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [myPayments, setMyPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.event_id || event?.id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, event]);

  const fetchData = async () => {
    const eventId = user?.event_id || event?.id;
    if (!eventId) {
      setLoading(false);
      return;
    }

    try {
      const [announcementsRes, programsRes, paymentsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/event/announcements?event_id=${eventId}`),
        axios.get(`${API_URL}/api/event/programs?event_id=${eventId}`),
        axios.get(`${API_URL}/api/event/payments/my`),
        axios.get(`${API_URL}/api/event/stats?event_id=${eventId}`)
      ]);
      setAnnouncements(announcementsRes.data.slice(0, 3));
      setPrograms(programsRes.data.slice(0, 2));
      setMyPayments(paymentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (announcementId) => {
    try {
      await axios.post(`${API_URL}/api/event/announcements/${announcementId}/acknowledge`);
      toast.success('Acknowledged!');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const unacknowledgedCount = stats?.unacknowledged_announcements || announcements.filter(
    a => !a.acknowledged_by?.includes(user?.id)
  ).length;

  const pendingPayments = myPayments.filter(p => p.status === 'pending').length;
  const verifiedPayments = myPayments.filter(p => p.status === 'verified').length;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Welcome Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-maroon to-maroon-hover p-8 text-white">
          <div className="relative z-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold">
              Let's celebrate, {user?.first_name}!
            </h1>
            <p className="mt-2 text-white/80 max-w-lg">
              Welcome to batchm8s! Stay updated with the latest announcements and don't forget to RSVP to the events.
            </p>
            {user?.batch_year && (
              <Badge className="mt-4 bg-gold text-cream-dark text-sm px-4 py-1">
                BATCH {user.batch_year}
              </Badge>
            )}
          </div>
          <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute right-20 bottom-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2" />
        </div>

        {/* Event Countdown */}
        <CountdownWidget eventDate={event?.event_date} eventName={event?.event_name} />

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Bell className="h-8 w-8 mx-auto text-maroon mb-2" />
              <p className="text-2xl font-bold text-gray-900">{unacknowledgedCount}</p>
              <p className="text-sm text-gray-500">Unread</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Calendar className="h-8 w-8 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats?.total_programs || programs.length}</p>
              <p className="text-sm text-gray-500">Programs</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Clock className="h-8 w-8 mx-auto text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{pendingPayments}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{verifiedPayments}</p>
              <p className="text-sm text-gray-500">Paid</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Announcements */}
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Bell className="h-5 w-5 text-maroon" />
                  Announcements
                </CardTitle>
                <CardDescription>Latest updates from organizers</CardDescription>
              </div>
              <Link to="/attendee/announcements">
                <Button variant="ghost" size="sm" className="text-maroon">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No announcements yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {announcements.map((ann) => {
                    const isAcknowledged = ann.acknowledged_by?.includes(user?.id);
                    return (
                      <div 
                        key={ann.id} 
                        className={`p-4 rounded-xl border transition-all ${
                          isAcknowledged 
                            ? 'bg-gray-50 border-gray-100' 
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={`text-xs ${
                                ann.priority === 'urgent' ? 'border-red-300 text-red-600' :
                                ann.priority === 'important' ? 'border-amber-300 text-amber-600' :
                                'border-green-300 text-green-600'
                              }`}>
                                {ann.priority}
                              </Badge>
                              {isAcknowledged && (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              )}
                            </div>
                            <p className="font-medium text-gray-900">{ann.title}</p>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{ann.content}</p>
                          </div>
                          {!isAcknowledged && (
                            <Button
                              size="sm"
                              onClick={() => handleAcknowledge(ann.id)}
                              className="bg-maroon hover:bg-maroon-hover text-white shrink-0"
                            >
                              Acknowledge
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Programs */}
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-maroon" />
                  Event Programs
                </CardTitle>
                <CardDescription>Don't miss these activities!</CardDescription>
              </div>
              <Link to="/attendee/program">
                <Button variant="ghost" size="sm" className="text-maroon">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {programs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No programs scheduled yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {programs.map((program) => (
                    <div 
                      key={program.id} 
                      className="p-4 rounded-xl border border-gray-200 hover:border-maroon/30 transition-colors"
                    >
                      <h3 className="font-heading text-lg font-semibold text-maroon">
                        {program.title}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(program.program_date)}
                        </span>
                      </div>
                      <div className="mt-3">
                        <Badge variant="outline" className="text-xs">
                          {program.activities?.length || 0} activities
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Reminder */}
        {pendingPayments > 0 && (
          <Card className="border-0 shadow-sm bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-amber-100">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">Payment Pending</h3>
                  <p className="text-sm text-amber-600">
                    You have {pendingPayments} payment(s) awaiting verification.
                  </p>
                </div>
                <Link to="/attendee/payments">
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                    View Payments
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;
